// Sensitive File

import {
  AlertDuration,
  AlertManager,
  AlertType,
  type StrictNullable,
  useAlertManager,
  useClassFactory,
  useConfig,
  useNetworkService,
} from "@pasta/box-v2";
import { Deferred } from "@pasta/deferred";
import { SentryClient, useSentry } from "@pasta/front-sentry-v2";
import axios, { type AxiosInstance } from "axios";
import { reactive } from "vue";
import { NoReaderChosenError } from "../errors/scws.error";
import { useSimpleFeatureStore } from "../stores/simple-features.store";
import {
  type SCWSCertStore,
  type SCWSCreateEnvResponse,
  type SCWSObject,
  type SCWSReader,
  type SCWSToken,
  type SCWSTokenPin,
} from "../types/scws.types";
import { usePastaWebsocket } from "./usePastaWebsocket";

type SentryBreadcrumb = Parameters<SentryClient["addBreadcrumb"]>[0];

export enum SCWSUrls {
  GET_CHALLENGE = "/dyn/get_challenge",
  CREATE_ENV = "/dyn/create_env",
  GET_READERS = "/dyn/get_readers",
  CONNECT = "/dyn/connect",
  GET_TOKEN_INFO = "/dyn/get_token_info",
  DISCONNECT = "/dyn/disconnect",
  GET_TOKEN_OBJECTS = "/dyn/get_token_objects",
  LOGIN = "/dyn/login",
  SIGN = "/dyn/sign",
  GET_OBJECT_DATA = "/dyn/get_object_data",
  EXPORT_OBJECT = "/dyn/export_object",
  START_AUTO_LOGIN = "dyn/start_auto_login",
  STOP_AUTO_LOGIN = "dyn/stop_auto_login",
  GET_CERT_STORES = "dyn/get_certstores",
  GET_CERT_STORE_OBJECTS = "dyn/get_certstore_certificates",
}

export const PADES_SIGN_EVT = "pades-sign";
export const PADES_ASK_CERT_EVT = "pades-ask-certificate";
export const SOFT_READER_NAME = "\\SOFTREADER";

export const SCWS_STARTING_PORT = 41_231;

export class SCWSApi {
  private http: AxiosInstance;
  private envId: StrictNullable<string> = null;

  public readers: SCWSReader[] = [];
  private selectedReaderIndex = -1;
  private token: StrictNullable<SCWSToken> = null;
  private objects: SCWSObject[] = [];
  private _initialized = false;
  private signPinUnlocked = false;

  private signatureCertificate: StrictNullable<SCWSObject> = null;
  private signatureCertificatePem: StrictNullable<string> = null;
  private signaturePrivateKey: StrictNullable<SCWSObject> = null;

  private wsListeners: Function[] = [];

  private alertManager: AlertManager;
  private sentry: SentryClient;

  public massModalProps: {
    open: boolean;
  };

  public chooseReaderModalProps: {
    open: boolean;
  };

  public chooseReaderModalDeferred: Deferred<number> = new Deferred();

  constructor() {
    this.http = axios.create({});

    this.http.interceptors.request.use(config => {
      if (this.envId) {
        config.params = { ...(config.params || {}), env: this.envId };
      }
      return config;
    });

    this.alertManager = useAlertManager();
    this.sentry = useSentry();

    this.massModalProps = reactive({
      open: false,
    });

    this.chooseReaderModalProps = reactive({
      open: false,
    });
  }

  async setupWsListeners() {
    this.addSentryBreadCrumb({ message: "setupWsListeners", level: "debug" });
    const ws = usePastaWebsocket();

    const listeners = [
      await ws.on("sign", PADES_SIGN_EVT, async (data: { hash: string }) => {
        this.addSentryBreadCrumb({ message: "on ws 'sign' event" });
        const signature = await this.sign(data.hash);
        ws.emit("provideSignature", { signature });
      }),
      await ws.on("askCertificate", PADES_ASK_CERT_EVT, async () => {
        this.addSentryBreadCrumb({ message: "on ws 'askCertificate' event" });
        ws.emit("provideCertificate", {
          certificate: this.signatureCertificatePem,
        });
      }),
    ];

    this.wsListeners = listeners;
  }

  async startAutoLogin() {
    this.addSentryBreadCrumb({ message: "startAutoLogin" });
    return (
      await this.http.get(SCWSUrls.START_AUTO_LOGIN, {
        // params: {
        //   counter: 2,
        // },
      })
    ).data;
  }

  async stopAutoLogin() {
    this.addSentryBreadCrumb({ message: "STOP_AUTO_LOGIN" });
    return (await this.http.get(SCWSUrls.STOP_AUTO_LOGIN)).data;
  }

  /**
   * As the middleware server is started on the client side, we need to find it
   * and set the base URL for the axios instance.
   *
   * According to the SCWS original sdk, the middleware server should be started on the
   * first port of a range of 10 ports, starting from SCWS_STARTING_PORT.
   *
   * When the middleware server is found, the base URL of the axios instance is set to the
   * found port and a challenge is retrieved for authentication.
   *
   * The search for the middleware server (the only slow step) stops with an AbortError
   * as soon as the given signal is aborted.
   */
  async init(signal?: AbortSignal) {
    this.addSentryBreadCrumb({
      message: "init - start",
      data: { initialized: this._initialized },
    });
    try {
      if (this._initialized) {
        return;
      }

      const { enabled, webappCert } = useSimpleFeatureStore().pades;

      if (!enabled || !webappCert) {
        return;
      }

      let port = SCWS_STARTING_PORT;
      let challenge: StrictNullable<string> = null;
      let tryCount = 20;
      const webappCerts = webappCert.split("|");

      this.addSentryBreadCrumb({
        message: "init - fetchChallenge",
        level: "debug",
      });
      while (!challenge && tryCount > 0) {
        for (const cert of webappCerts) {
          signal?.throwIfAborted();
          try {
            const response = await this.fetchChallenge(`http://127.0.0.1:${port}`, cert, signal);
            challenge = response.challenge;
            this.http.defaults.baseURL = response.url;
            break;
          } catch (e) {
            console.warn(e);
          }
        }

        if (!challenge) {
          for (const cert of webappCerts) {
            signal?.throwIfAborted();
            try {
              const response = await this.fetchChallenge(`https://127.0.0.1:${port + 1}`, cert, signal);
              challenge = response.challenge;
              this.http.defaults.baseURL = response.url;
              break;
            } catch (e) {
              console.warn(e);
              port += 10;
            }
          }
        }
        tryCount--;
      }

      signal?.throwIfAborted();

      if (!challenge) {
        throw new Error("Could not find SCWS middleware server");
      }

      const networkService = useNetworkService();
      this.addSentryBreadCrumb({
        message: "init -  fetch challenge response",
        level: "debug",
      });
      const challengeResponse = (
        await networkService.post("/pades/challenge", {
          challenge,
        })
      ).data.result;

      this.addSentryBreadCrumb({
        message: "init - validateChallengeResponse",
        level: "debug",
      });
      const response = await this.validateChallengeResponse(challengeResponse);
      this.envId = response.envId;

      this.addSentryBreadCrumb({
        message: "init - fetchAllReaders",
        level: "debug",
      });
      await this.fetchAllReaders();
      this._initialized = true;
    } catch (e) {
      if (e instanceof NoReaderChosenError) {
        await this.logoff();
        return;
      }
      throw e;
    }

    this.addSentryBreadCrumb({ message: "init - end" });
  }

  /**
   * Try fetching the challenge from the middleware server at the given base URL.
   *
   * @param baseURL The base URL (protocol, host and port) of the middleware server.
   * @param cert The certificate to use for authentication.
   * @param signal Aborts the request when triggered.
   * @returns  The challenge and the base URL of the middleware server.
   */
  private async fetchChallenge(
    baseURL: string,
    cert: string,
    signal?: AbortSignal
  ): Promise<{ challenge: string; url: string }> {
    const url = `${baseURL}${SCWSUrls.GET_CHALLENGE}`;

    return {
      challenge: (
        await this.http.get(url, {
          params: {
            token: cert,
          },
          timeout: 2000,
          signal,
        })
      ).data,
      url: baseURL,
    };
  }

  /**
   * Validates the challenge response with the middleware server.
   * @param challengeResponse The challenge response to validate.
   * @returns The response of the middleware server,
   * including the environment ID and the public key.
   */
  private async validateChallengeResponse(challengeResponse: string): Promise<SCWSCreateEnvResponse> {
    return (
      await this.http.get(SCWSUrls.CREATE_ENV, {
        params: {
          sig: challengeResponse,
          encalg: "SHA-256",
        },
      })
    ).data;
  }

  /**
   * Fetches all readers connected to the middleware server.
   *
   * Currently, only the first reader is used.
   *
   * @returns The list of readers.
   */
  private async fetchAllReaders() {
    try {
      const readers: SCWSReader[] = (await this.http.get(SCWSUrls.GET_READERS)).data;
      let igcCerts: SCWSObject[] = [];

      try {
        const myStore = (await this.http.get(SCWSUrls.GET_CERT_STORES)).data.stores.find(
          (s: SCWSCertStore) => s.systemName === "MY"
        );

        if (!myStore) {
          throw new Error("Could not find cert stores");
        }

        igcCerts = (
          await this.http.get(SCWSUrls.GET_CERT_STORE_OBJECTS, {
            params: {
              store: myStore.handle,
            },
          })
        ).data.objects.filter((cert: SCWSObject) => {
          return (
            cert.type === "certificate" &&
            cert.issuerName === "AC AGENTS" &&
            cert.usages?.map(u => u.shortName).includes("clientAuth")
          );
        });
      } catch (e) {
        console.warn("Error fetching IGC certificates:", e);
      }

      this.readers =
        igcCerts.length > 0
          ? [
              ...readers,
              {
                name: SOFT_READER_NAME,
                card: true,
                status: "ok",
                type: "softtoken",
              },
            ]
          : readers;
      return this.readers;
    } catch {
      return [];
    }
  }

  /**
   * Connects to the first reader to retrieve informations about the current token.
   */
  private async connect() {
    this.addSentryBreadCrumb({
      message: "connect - start",
      data: { readers: this.readers.length },
    });
    if (this.readers.length === 0) {
      this.alertManager.createAlert({
        message: `Aucun périphérique de signature connecté`,
        type: AlertType.WARNING,
        duration: AlertDuration.MEDIUM,
      });
      throw new Error("No reader found");
    }

    const config = useConfig<any>();
    if (this.readers.length > 1 && !config.disableChooseReaderModal) {
      this.chooseReaderModalProps.open = true;
      try {
        this.addSentryBreadCrumb({
          message: "connect - chooseReaderModal",
          level: "debug",
        });
        this.selectedReaderIndex = (await this.chooseReaderModalDeferred) ?? 0;
      } catch (e) {
        this.chooseReaderModalProps.open = false;
        this.chooseReaderModalDeferred = new Deferred();
        throw e;
      }
      this.chooseReaderModalProps.open = false;
    } else {
      this.selectedReaderIndex = 0;
    }

    this.addSentryBreadCrumb({
      message: "connect - CONNECT",
      level: "debug",
    });
    const { handle } = (
      await this.http.get(SCWSUrls.CONNECT, {
        params: { reader: this.reader.name },
      })
    ).data;

    this.addSentryBreadCrumb({
      message: "connect - GET_TOKEN_INFO",
      level: "debug",
    });
    this.token = {
      handle,
      ...(
        await this.http.get(SCWSUrls.GET_TOKEN_INFO, {
          params: { token: handle },
        })
      ).data,
    };

    await this.fetchTokenObjects();
    this.addSentryBreadCrumb({ message: "connect - end" });
  }

  private isMteCardCertificate(certificate: SCWSObject): boolean {
    return certificate.container === "sign" || certificate.ckLabel === "sign";
  }

  private isCertignaCertificate(certificate: SCWSObject): boolean {
    return !!certificate.issuerName?.startsWith("Certigna");
  }

  private isIgcCertificate(certificate: SCWSObject): boolean {
    return !!(
      certificate.issuerName?.startsWith("AC AGENTS") &&
      certificate.usages?.map(u => u.shortName).includes("clientAuth")
    );
  }

  private isSoftToken() {
    return this.reader.type === "softtoken";
  }

  /**
   * Unlocks the token for signature.
   *
   * The signal is checked between steps: the flow stops with an AbortError once the
   * step in progress (e.g. the device's PIN prompt, which can't be interrupted) returns.
   **/
  public async unlockPinForSignature(signal?: AbortSignal) {
    this.addSentryBreadCrumb({
      message: "unlockPin - start",
      data: { signPinUnlocked: this.signPinUnlocked },
    });
    if (this.signPinUnlocked) {
      this.alertManager.createAlert({
        message: "Le code PIN est déjà déverrouillé. Réutilisation de la précédente session de signature.",
        type: AlertType.INFO,
        duration: AlertDuration.SHORT,
      });
      return this.signPinUnlocked;
    }

    if (!this._initialized) {
      return;
    }
    await this.connect();
    signal?.throwIfAborted();

    this.signatureCertificate =
      this.objects
        .filter(o => o.type === "certificate")
        .find(o => {
          if (o.container === "sign" || o.ckLabel === "sign") {
            // MTE Cards
            return true;
          }

          if (o.ckLabel?.startsWith("Certificat de signature")) {
            // MI Cards
            return true;
          }

          if (o.issuerName?.startsWith("Certigna")) {
            // Certigna USB dongles
            return true;
          }

          if (o.issuerName?.startsWith("AC AGENTS") && o.usages?.map(u => u.shortName).includes("clientAuth")) {
            // IGC Certs
            return true;
          }

          return false;
        }) || null;

    if (!this.signatureCertificate) {
      console.log("No signature certificate found", this.objects);
      throw new Error("No signature certificate found before unlocking");
    }

    const pin = this.pins.at(this.signatureCertificate!.vslot)!;
    if (!pin && !this.isSoftToken()) {
      this.alertManager.createAlert({
        message: "Le code PIN lié au certificat de signature n'a pas été trouvé.",
        type: AlertType.ERROR,
        duration: AlertDuration.SHORT,
      });
      throw new Error("No pin found");
    }

    try {
      if (!this.isSoftToken()) {
        this.addSentryBreadCrumb({
          message: "unlockPin - login",
          level: "debug",
        });
        await this.login(pin);
      }
    } catch (e) {
      this.sentry.captureException(e as Error);
      return false;
    }
    signal?.throwIfAborted();

    this.addSentryBreadCrumb({
      message: "unlockPin - GET_TOKEN_INFO",
      level: "debug",
    });
    this.token = {
      handle: this.token!.handle,
      ...(
        await this.http.get(SCWSUrls.GET_TOKEN_INFO, {
          params: { token: this.token?.handle },
        })
      ).data,
    };

    await this.fetchTokenObjects();

    this.signatureCertificate = this.objects.find(o => o.handle === this.signatureCertificate?.handle) || null;

    if (!this.signatureCertificate) {
      throw new Error("No signature certificate found after unlocking");
    }

    this.addSentryBreadCrumb({
      message: "unlockPin - exportObject",
      level: "debug",
    });
    this.signatureCertificatePem = await this.exportObject(this.signatureCertificate.handle, "PEM");

    this.signaturePrivateKey =
      this.objects.find(o => o.type == "privateKey" && o.ckId === this.signatureCertificate?.ckId) || null;

    if (!this.signaturePrivateKey) {
      throw new Error("No signature private key found after unlocking");
    }

    await this.setupWsListeners();

    this.addSentryBreadCrumb({ message: "unlockPin - end" });
    this.signPinUnlocked = true;
    return this.signPinUnlocked;
  }

  /**
   * Export an object from the token.
   *
   * In our case, we use it to export the certificate as a PEM file
   *
   * @param handle The handle of the object to export.
   * @param format The format to export the object to.
   */
  private async exportObject(handle: string, format?: string): Promise<string> {
    return (
      await this.http.get(SCWSUrls.EXPORT_OBJECT, {
        params: {
          object: handle,
          format,
        },
      })
    ).data;
  }

  /**
   * Submit the user's credentials to the middleware server.
   *
   * @param credentials The credentials to submit as returned by the requestCredentials method.
   * @param pin The pin to unlock.
   * @returns Whether the login was successful.
   */
  private async login(pin: SCWSTokenPin) {
    this.addSentryBreadCrumb({ message: "login", data: { pin } });
    await this.startAutoLogin();
    try {
      return (
        await this.http.post(SCWSUrls.LOGIN, "#\n", {
          params: {
            token: this.token?.handle,
            pin: pin.Vslotflags,
          },
        })
      ).data;
    } catch (e: any) {
      console.warn(e.response?.data);
      await this.stopAutoLogin();
      throw e;
    }
  }

  /**
   * Terminates the session with the middleware server.
   */
  public async logoff() {
    this.addSentryBreadCrumb({
      message: "logoff",
      data: { hasToken: !!this.token },
    });
    if (this.token) {
      await this.http.get(SCWSUrls.DISCONNECT, {
        params: {
          token: this.token.handle,
        },
      });
      await this.stopAutoLogin();
    }

    for (const listener of this.wsListeners) {
      listener(); // Unsubscribe
    }

    this.wsListeners = [];
    this.signPinUnlocked = false;
    this.signatureCertificate = null;
    this.signatureCertificatePem = null;
    this.signaturePrivateKey = null;
    this.token = null;
    this.readers = [];
    this.objects = [];
    this._initialized = false;
    this.selectedReaderIndex = -1;
    this.chooseReaderModalDeferred = new Deferred();
  }

  /**
   * Fetches all the cryptographic objects stored on the token.
   *
   * @returns The list of objects.
   */
  public async fetchTokenObjects() {
    this.addSentryBreadCrumb({
      message: "fetchTokenObjects",
      data: { hasToken: !!this.token },
    });
    if (!this.token) {
      throw new Error("No token found");
    }

    this.objects = (
      await this.http.get(SCWSUrls.GET_TOKEN_OBJECTS, {
        params: {
          token: this.token.handle,
        },
      })
    ).data?.objects;

    return this.objects;
  }

  /**
   * Send a hash to the middleware server to sign it with the token.
   *
   * @param data The hash to sign.
   * @returns The signed hash.
   */
  public async sign(data: string) {
    this.addSentryBreadCrumb({ message: "sign - start" });
    const formData = new FormData();
    formData.append("hash", data);

    const { data: result } = await this.http.post(SCWSUrls.SIGN, formData, {
      params: {
        key: this.signaturePrivateKey!.handle,
        hashAlgorithm: "sha256",
      },
    });

    this.addSentryBreadCrumb({ message: "sign - end" });

    return result;
  }

  /**
   * The main reader connected to the middleware server.
   */
  get reader() {
    return this.readers[this.selectedReaderIndex];
  }

  /**
   * As strange as it may seem, the SCWS middleware returns the pins of the token under
   * the `infos` key.
   */
  get pins() {
    return (this.token?.infos || []).map((pin, index) => {
      return {
        ...pin,
        Vslotflags: index, // For compatibility between SCWS versions
      };
    });
  }

  get initialized() {
    return this._initialized;
  }

  private addSentryBreadCrumb(args: SentryBreadcrumb) {
    this.sentry.addBreadcrumb({ level: "info", ...args, category: "scws" });
  }
}

export const [swcsToken, useSCWS] = useClassFactory(SCWSApi);

/**
 * Converts a hexadecimal string to a Uint8Array.
 * @param hex The hexadecimal string to convert.
 * @returns The Uint8Array.
 */
export function fromHexString(hex: string) {
  return new Uint8Array((hex.match(/[\da-fA-F]{2}/gi) || []).map(h => parseInt(h, 16))).buffer;
}

/**
 * Converts a Buffer/Uint8Array to a hexadecimal string.
 * @param buffer The buffer to convert.
 * @returns The hexadecimal string.
 */
export function toHexString(buffer: Uint8Array | ArrayBuffer) {
  return Array.from(new Uint8Array(buffer))
    .map(x => `00${x.toString(16)}`.slice(-2))
    .join("");
}

export async function preventCloseWindow(e: BeforeUnloadEvent) {
  e.preventDefault();
  return true;
}
